import type { components } from '@pba/types-openapi-client';
import type { AppLanguage } from 'i18n/supported-app-languages-names';
import i18next from 'i18next';
import { ChangeRequest } from './change-request';
import { type Entity, createEntity } from './entity';
import { type Image, createImage } from './image';
import { type NotificationSetting, createNotificationSetting } from './notifications';
import { type OrganizationList, createOrganizationList } from './organization';
import type { CommunityPrivacy } from './privacy';
import type { ProgramEventType } from './program-event';
import type {
  ChangeRequestDataObject,
  CommunityBaseDataObject,
  CommunityDetailDataObject,
  CommunityMemberDataObject,
  CommunityMetaDataObject,
  CommunityMetaItemDataObject,
  CommunityNotificationSettingsDataObject,
} from './service';
import type { Tag } from './tag';
import { createUploadedFile } from './uploaded-file';
import { type UserList, createUserList } from './user';

type PushNotificationsSettings = {
  enableNotifications: NotificationSetting;
  events: NotificationSetting;
  giving: NotificationSetting;
  newsfeed: NotificationSetting;
  program: NotificationSetting;
};

const createPushNotificationsSettings = (data: CommunityNotificationSettingsDataObject): PushNotificationsSettings => ({
  enableNotifications: createNotificationSetting(data.push_notifications_settings.enable_notifications),
  events: createNotificationSetting(data.push_notifications_settings.events),
  giving: createNotificationSetting(data.push_notifications_settings.giving),
  newsfeed: createNotificationSetting(data.push_notifications_settings.newsfeed),
  program: createNotificationSetting(data.push_notifications_settings.program),
});

type EmailNotificationsSettings = {
  enableNotifications: NotificationSetting;
  events: NotificationSetting;
  giving: NotificationSetting;
  newsfeed: NotificationSetting;
  program: NotificationSetting;
};

const createEmailNotificationsSettings = (
  data: CommunityNotificationSettingsDataObject,
): EmailNotificationsSettings => ({
  enableNotifications: createNotificationSetting(data.email_notifications_settings.enable_notifications),
  events: createNotificationSetting(data.email_notifications_settings.events),
  giving: createNotificationSetting(data.email_notifications_settings.giving),
  newsfeed: createNotificationSetting(data.email_notifications_settings.newsfeed),
  program: createNotificationSetting(data.email_notifications_settings.program),
});

type SmsNotificationsSettings = {
  enableNotifications?: NotificationSetting;
  invitations?: NotificationSetting;
};

const createSmsNotificationsSettings = (data: CommunityNotificationSettingsDataObject): SmsNotificationsSettings => ({
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  enableNotifications: data.sms_notifications_settings?.enable_notifications
    ? createNotificationSetting(data.sms_notifications_settings.enable_notifications)
    : undefined,
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  invitations: data.sms_notifications_settings?.invitations
    ? createNotificationSetting(data.sms_notifications_settings.invitations)
    : undefined,
});

export type CommunityNotificationSettings = {
  emailNotificationsSettings: EmailNotificationsSettings;
  pushNotificationsSettings: PushNotificationsSettings;
  smsNotificationsSettings: SmsNotificationsSettings;
};

export const createCommunityNotificationSettings = (
  data: CommunityNotificationSettingsDataObject,
): CommunityNotificationSettings => ({
  emailNotificationsSettings: createEmailNotificationsSettings(data),
  pushNotificationsSettings: createPushNotificationsSettings(data),
  smsNotificationsSettings: createSmsNotificationsSettings(data),
});

export type CommunitySettings = {
  allowComments?: boolean;
  allowedEventsAdmin?: ProgramEventType[];
  allowedEventsMember?: ProgramEventType[];
  allowInvites?: boolean;
  allowPosts?: boolean;
  allowUnsubscribe?: boolean;
  viewMembers?: boolean;
};

export const createCommunitySettings = (data: components['schemas']['SettingsBase']): CommunitySettings => ({
  allowComments: data.allow_comments,
  // TODO: remove this once rails api is updated
  allowedEventsAdmin: data.allowed_events_admin?.filter((eventType) => eventType !== 'telehealth_visit'),
  // TODO: remove this once rails api is updated
  allowedEventsMember: data.allowed_events_member?.filter((eventType) => eventType !== 'telehealth_visit'),
  allowInvites: data.allow_invites,
  allowPosts: data.allow_posts,
  allowUnsubscribe: data.allow_unsubscribe,
  viewMembers: data.view_members,
});

export type MetaItem = {
  children?: MetaItem[];
  getTranslation: (language?: AppLanguage) => string;
  name: string;
  translations: MetaItemTranslations;
};

export type MetaItemTranslations = Record<AppLanguage, string>;

// TODO: move getTranslation outside
const createMetaItem = (data: CommunityMetaItemDataObject): MetaItem => ({
  children: data.children?.map((metaItem) => createMetaItem(metaItem)),
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  getTranslation: (language = i18next.language as AppLanguage) => data.translations[language],
  name: data.name,
  translations: data.translations,
});

export type CommunityMeta = {
  patientTypes?: MetaItem[];
  serviceTypes?: MetaItem[];
  telehealthVisitDurations?: number[];
};

export const createCommunityMeta = (data: CommunityMetaDataObject): CommunityMeta => ({
  patientTypes: data.patient_types?.map((metaItem) => createMetaItem(metaItem)),
  serviceTypes: data.service_types?.map((metaItem) => createMetaItem(metaItem)),
  telehealthVisitDurations: data.telehealth_visit_durations,
});

export type CommunitySlim = {
  coverPhoto: Image;
  organizationId: string;
  privacy: CommunityPrivacy;
  profilePhoto?: Image;
  settings: CommunitySettings;
} & Entity;

export const createCommunitySlim = (data: components['schemas']['CommunitySlim']): CommunitySlim => ({
  // eslint-disable-next-line @typescript-eslint/no-misused-spread -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  ...createEntity(data),
  coverPhoto: createImage(data.cover_photo),
  organizationId: data.organization_id,
  privacy: data.privacy,
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- OpenAPI marks profile_photo as always present, but API responses may omit it
  profilePhoto: data.profile_photo ? createImage(data.profile_photo) : undefined,
  settings: createCommunitySettings(data.settings),
});

export type CommunityBase = {
  coverPhoto?: Image;
  membersCount: number;
} & Entity;

export const createCommunityBase = (data: CommunityBaseDataObject): CommunityBase => ({
  // eslint-disable-next-line @typescript-eslint/no-misused-spread -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  ...createEntity(data),
  coverPhoto: data.cover_photo ? createImage(data.cover_photo) : undefined,
  membersCount: data.members_count,
});

export type CommunityListGlobal = {
  autoJoin: boolean;
  coverPhoto?: Image;
  description: string;
  htmlDescription: string;
  membersCount: number;
  meta?: CommunityMeta;
  organization: OrganizationList;
  privacy: CommunityPrivacy;
  profilePhoto?: Image;
  settings: CommunitySettings;
  tags: Tag[];
} & CommunitySlim;

export const createCommunityListGlobal = (data: components['schemas']['CommunityListGlobal']): CommunityListGlobal => ({
  // eslint-disable-next-line @typescript-eslint/no-misused-spread -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  ...createCommunitySlim(data),
  autoJoin: data.auto_join,
  coverPhoto: createImage(data.cover_photo),
  description: data.description,
  htmlDescription: data.html_description,
  membersCount: data.members_count,
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  meta: data.meta ? createCommunityMeta(data.meta) : undefined,
  organization: createOrganizationList(data.organization),
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- OpenAPI marks profile_photo as always present, but API responses may omit it
  profilePhoto: data.profile_photo ? createImage(data.profile_photo) : undefined,
  tags: data.tags,
});

export type CommunityList = CommunityListGlobal;

export const createCommunityList = (data: components['schemas']['CommunityListGlobal']): CommunityList => ({
  // eslint-disable-next-line @typescript-eslint/no-misused-spread -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  ...createCommunityListGlobal(data),
});

export type CommunityDetail = {
  administrators: CommunityAdmin[];
  changeRequests: ChangeRequest[];
  default: boolean;
} & CommunityList;

export const createCommunityDetail = (data: CommunityDetailDataObject): CommunityDetail => ({
  // eslint-disable-next-line @typescript-eslint/no-misused-spread -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  ...createCommunityList(data),
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  administrators: data.administrators?.map((administrator) => createCommunityAdmin(administrator)),
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  changeRequests: data.change_requests?.map((request: ChangeRequestDataObject) => new ChangeRequest(request)),
  default: data.default,
});

type CommunityAdmin = UserList;

const createCommunityAdmin = (data: components['schemas']['UserList']): CommunityAdmin => ({
  ...createUserList(data),
});

export const hasChangeRequestStatus = ({
  community,
  status,
}: {
  community: CommunityDetail;
  status: ChangeRequest['status'];
}): boolean =>
  community.changeRequests.some((request) => request.kind === 'community_request_to_join' && request.status === status);

/**
 * @deprecated
 * BE should use some reusable user model. For now it is full custom
 */
export type CommunityMember = Pick<UserList, 'email' | 'firstName' | 'id' | 'lastName' | 'profession' | 'profilePhoto'>;

export const createCommunityMember = (data: CommunityMemberDataObject): CommunityMember => ({
  email: data.email,
  firstName: data.first_name,
  id: data.id,
  lastName: data.last_name,
  profession: data.profession,
  profilePhoto: data.profile_photo ? createUploadedFile(data.profile_photo) : undefined,
});
